import { useEffect, useRef, useState } from "react";
import "./styles.css";
import {
  Check,
  ClipboardList,
  Target,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

import AuthScreen from "./components/AuthScreen";
import ConfirmModal from "./components/ConfirmModal";
import CreateScreen from "./components/CreateScreen";
import EventsScreen from "./components/EventsScreen";
import ExecutorScreen from "./components/ExecutorScreen";
import TaskTokenScreen from "./components/TaskTokenScreen";
import TimelineView from "./components/TimelineView";
import ContractorTaskListScreen from "./components/ContractorTaskListScreen";
import ContractorsScreen from "./components/ContractorsScreen";
import UserScreen from "./components/UserScreen";
import UserProfileButton from "./components/UserProfileButton";
import { InlineIcon, UiIcon } from "./components/Icon";
import { API_BASE, AUTH_STORAGE_KEY } from "./config/appConfig";
import { STATUS } from "./config/taskData";
import { normalizeErrorMessage } from "./utils/errors";
import { getDeadlineParts } from "./utils/tasks";

// Status mapping helpers used across all screens.
const BACKEND_TO_UI_STATUS = {
  new: "new",
  accepted: "in_progress",
  completed: "done",
  issue: "problem",
};

const UI_TO_BACKEND_STATUS = {
  new: "new",
  in_progress: "accepted",
  done: "completed",
  problem: "issue",
};

const formatRoleText = (position, company) => {
  const pos = (position || "").trim();
  const comp = (company || "").trim();
  if (pos && comp) return `${pos} | ${comp}`;
  if (pos) return pos;
  if (comp) return comp;
  return "Wykonawca";
};

const parseDeadlineParts = (deadline) => {
  return getDeadlineParts(deadline);
};

const toUtcIsoFromLocalDateTime = (date, time) => {
  if (!date) return null;
  const localDateTime = new Date(`${date}T${time || "23:59"}:00`);
  if (Number.isNaN(localDateTime.getTime())) return null;
  return localDateTime.toISOString();
};

const SESSION_WARNING_MS = 2 * 60 * 1000;

const parseJwtExpiryMs = (accessToken) => {
  if (!accessToken || typeof accessToken !== "string") return null;

  try {
    const payloadPart = accessToken.split(".")[1];
    if (!payloadPart) return null;

    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
    const decoded = JSON.parse(atob(padded));
    if (typeof decoded?.exp !== "number") return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
};

function useIsOffline() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);
  return isOffline;
}

export default function App() {
  const isOffline = useIsOffline();

  // Authentication state loaded from localStorage.
  const [auth, setAuth] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Main screen and data state used by dashboard + event/task views.
  const [screen, setScreen] = useState("events");
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [userTier, setUserTier] = useState(null);
  const [userLimits, setUserLimits] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tasksByEvent, setTasksByEvent] = useState({});
  const [contractorsByEvent, setContractorsByEvent] = useState({});
  const [activeTask, setActiveTask] = useState(null);
  const [openContractorEditorOnEnter, setOpenContractorEditorOnEnter] = useState(false);
  const [filter, setFilter] = useState("active");
  const [form, setForm] = useState({
    desc: "",
    location: "",
    contactId: null,
    notifySms: false,
    notifyEmail: false,
    priority: "normal",
    date: "",
    time: "",
  });
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showContractorsModal, setShowContractorsModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [accessExpiresAtMs, setAccessExpiresAtMs] = useState(null);
  const sessionRefreshInFlightRef = useRef(false);


  const currentEventId = selectedEvent?.id || null;
  const tasks = currentEventId ? tasksByEvent[currentEventId] || [] : [];
  const contractors = currentEventId
    ? contractorsByEvent[currentEventId] || []
    : [];
  const assignableContacts = [
    {
      id: 0,
      name: "Ja (Koordynator)",
      position: "Koordynator",
      company: "",
      role: "Koordynator",
      phone: "",
    },
    ...contractors,
  ];

  // Converts backend task shape into the UI shape used by cards/timeline/executor.
  const normalizeTask = (task) => {
    const deadline = parseDeadlineParts(task.deadline);
    const contact = task.contractor_details
      ? {
          id: task.contractor_details.id,
          name: task.contractor_details.name,
          position: task.contractor_details.position || "",
          company: task.contractor_details.company || "",
          role: formatRoleText(
            task.contractor_details.position,
            task.contractor_details.company
          ),
          phone: task.contractor_details.phone || "",
        }
      : task.assignee_user_details
      ? {
          id: 0,
          name: "Ja (Koordynator)",
          position: "Koordynator",
          company: "",
          role: "Koordynator",
          phone: "",
        }
      : null;

    return {
      id: task.id,
      name: task.name || task.description || "Bez nazwy",
      desc: task.description || task.name,
      location: task.location || "",
      contact,
      priority: "normal",
      status: BACKEND_TO_UI_STATUS[task.status] || "new",
      date: deadline.date,
      time: deadline.time,
      deadlineAt: deadline.raw,
    };
  };

  // Small top toast notification shown for success/error feedback.
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const persistAuth = (nextAuth) => {
    setAuth(nextAuth);
    setAccessExpiresAtMs(parseJwtExpiryMs(nextAuth?.access));
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
  };

  const refreshAccessToken = async (refreshToken) => {
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${API_BASE}/auth/jwt/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.access) return null;
      return {
        access: data.access,
        refresh: data.refresh || refreshToken,
      };
    } catch {
      return null;
    }
  };

  const apiRequest = async (url, options = {}, allowRefresh = true) => {
    const currentAuth = auth;
    const headers = {
      ...(options.headers || {}),
      ...(currentAuth?.access
        ? { Authorization: `JWT ${currentAuth.access}` }
        : {}),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status !== 401 || !allowRefresh) return response;

    const refreshedTokens = await refreshAccessToken(currentAuth?.refresh);
    if (!refreshedTokens?.access) {
      logout();
      return response;
    }

    const nextAuth = {
      ...currentAuth,
      access: refreshedTokens.access,
      refresh: refreshedTokens.refresh,
    };
    persistAuth(nextAuth);

    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `JWT ${nextAuth.access}`,
      },
    });
  };

  // Sign-out action for "Wyloguj" buttons.
  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuth(null);
    setAccessExpiresAtMs(null);
    sessionRefreshInFlightRef.current = false;
    setUserTier(null);
    setUserLimits(null);
    setScreen("events");
    setActiveTask(null);
    setSelectedEvent(null);
    setEvents([]);
    setTasksByEvent({});
    setContractorsByEvent({});
  };

  // API: fetch usage limits and tier for current user.
  const fetchUserLimits = async () => {
    if (!auth?.access) return;

    try {
      const response = await apiRequest(`${API_BASE}/api/me/limits/`);
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
        }
        return;
      }
      if (typeof data?.tier === "string") setUserTier(data.tier);
      setUserLimits(data || null);
    } catch {
      setUserTier(null);
      setUserLimits(null);
    }
  };

  // API: fetch events list shown in EventsScreen.
  const fetchEvents = async () => {
    if (!auth?.access) return;
    setEventsLoading(true);
    setEventsError("");

    try {
      const response = await apiRequest(`${API_BASE}/api/events/`);

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        setEventsError(normalizeErrorMessage(data));
        return;
      }

      const list = Array.isArray(data) ? data : [];
      setEvents(list);

      if (selectedEvent) {
        const refreshedSelected = list.find((e) => e.id === selectedEvent.id);
        if (!refreshedSelected) {
          setSelectedEvent(null);
          setScreen("events");
        } else {
          setSelectedEvent(refreshedSelected);
        }
      }
    } catch {
      setEventsError("Nie udało się pobrać listy twoich wydarzeń.");
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.access) fetchEvents();
  }, [auth?.access]);

  useEffect(() => {
    if (auth?.access) fetchUserLimits();
  }, [auth?.access]);

  useEffect(() => {
    if (!auth?.access || !accessExpiresAtMs) {
      sessionRefreshInFlightRef.current = false;
      return;
    }

    const now = Date.now();
    const msUntilExpiry = accessExpiresAtMs - now;

    if (msUntilExpiry <= 0) {
      logout();
      return;
    }

    const warningTimeout = setTimeout(async () => {
      if (sessionRefreshInFlightRef.current) return;
      sessionRefreshInFlightRef.current = true;

      const refreshedTokens = await refreshAccessToken(auth?.refresh);
      if (!refreshedTokens?.access) {
        sessionRefreshInFlightRef.current = false;
        logout();
        return;
      }

      persistAuth({
        ...auth,
        access: refreshedTokens.access,
        refresh: refreshedTokens.refresh,
      });
      sessionRefreshInFlightRef.current = false;
    }, Math.max(0, msUntilExpiry - SESSION_WARNING_MS));

    const logoutTimeout = setTimeout(logout, msUntilExpiry);

    return () => {
      clearTimeout(warningTimeout);
      clearTimeout(logoutTimeout);
    };
  }, [auth?.access, auth?.refresh, accessExpiresAtMs]);

  // API: fetch tasks for currently selected event (dashboard list/timeline).
  const fetchTasksForEvent = async (eventId) => {
    if (!auth?.access || !eventId) return;

    try {
      const response = await apiRequest(`${API_BASE}/api/tasks/`);

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        showToast(normalizeErrorMessage(data));
        return;
      }

      const allTasks = Array.isArray(data) ? data : [];
      const eventTasks = allTasks
        .filter((task) => task.event === eventId)
        .map(normalizeTask);

      setTasksByEvent((previous) => ({
        ...previous,
        [eventId]: eventTasks,
      }));
    } catch {
      showToast("Nie udało się pobrać zadań.");
    }
  };

  useEffect(() => {
    if (currentEventId) fetchTasksForEvent(currentEventId);
  }, [currentEventId, auth?.access]);

  // API: fetch contractors for currently selected event (used in CreateScreen assignee list).
  const fetchContractorsForEvent = async (eventId) => {
    if (!auth?.access || !eventId) return;

    try {
      const response = await apiRequest(`${API_BASE}/api/contractors/?event=${eventId}`);

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        showToast(normalizeErrorMessage(data));
        return;
      }

      const list = Array.isArray(data) ? data : [];
      const mapped = list.map((contractor) => ({
        id: contractor.id,
        name: contractor.name,
        position: contractor.position || "",
        company: contractor.company || "",
        role: formatRoleText(contractor.position, contractor.company),
        phone: contractor.phone || "",
        email: contractor.email || "",
      }));

      setContractorsByEvent((previous) => ({
        ...previous,
        [eventId]: mapped,
      }));
    } catch {
      showToast("Nie udało się pobrać wykonawców.");
    }
  };

  useEffect(() => {
    if (currentEventId) fetchContractorsForEvent(currentEventId);
  }, [currentEventId, auth?.access]);

  // API: create new event from EventsScreen form.
  const createEvent = async (payload) => {
    try {
      const response = await apiRequest(`${API_BASE}/api/events/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      await fetchEvents();
      await fetchUserLimits();
      showToast(`Wydarzenie ${data.name} zostało utworzone`);
      return { ok: true, event: data };
    } catch {
      return { ok: false, error: "Nie udało się utworzyć wydarzenia." };
    }
  };

  // API: archive/restore event from event list and dashboard header.
  const setEventActive = async (event, active) => {
    try {
      const response = await apiRequest(`${API_BASE}/api/events/${event.id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ active }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      await fetchEvents();
      showToast(
        active
          ? `"${data.name}" zostało przywrócone`
          : `"${data.name}" zostało zarchiwizowane`
      );
      return { ok: true, event: data };
    } catch {
      return {
        ok: false,
        error: active
          ? "Nie udało się przywrócić wydarzenia."
          : "Nie udało się zarchiwizować wydarzenia.",
      };
    }
  };

  const archiveEvent = (event) => setEventActive(event, false);
  const restoreEvent = (event) => setEventActive(event, true);

  const updateEvent = async (eventId, payload) => {
    try {
      const response = await apiRequest(`${API_BASE}/api/events/${eventId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      await fetchEvents();
      showToast(`Wydarzenie ${data.name} zostało zaktualizowane`);
      return { ok: true, event: data };
    } catch {
      return { ok: false, error: "Nie udało się zaktualizować wydarzenia." };
    }
  };

  // API: create task from CreateScreen form.
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchTasksForEvent(currentEventId),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };
  const createTask = async () => {
    if (!form.desc.trim() || !currentEventId) return;
    if (isSubmittingTask) return;
    setIsSubmittingTask(true);

    try {
      const payload = {
        name: form.desc.trim().slice(0, 255),
        description: form.desc.trim(),
        location: form.location?.trim() || null,
        event: currentEventId,
        status: "new",
        deadline: toUtcIsoFromLocalDateTime(form.date, form.time),
        contractor: form.contactId && form.contactId > 0 ? form.contactId : null,
        assign_to_self: form.contactId === 0,
        notify_sms: Boolean(form.notifySms && form.contactId && form.contactId > 0),
        notify_email: Boolean(form.notifyEmail && form.contactId && form.contactId > 0),
      };

      const response = await apiRequest(`${API_BASE}/api/tasks/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        showToast(normalizeErrorMessage(data));
        return;
      }

      const task = normalizeTask(data);
      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: [task, ...eventTasks],
        };
      });

      showToast("Zadanie zapisane");
    } catch {
      showToast("Nie udało się zapisać zadania.");
      setIsSubmittingTask(false);
      return;
    }

    setIsSubmittingTask(false);
    setForm({
      desc: "",
      location: "",
      contactId: null,
      notifySms: false,
      notifyEmail: false,
      priority: "normal",
      date: "",
      time: "",
    });
    setShowCreateTaskModal(false);
    setScreen("dashboard");
  };

  // API: create contractor for currently selected event and append to assignee list.
  const createContractor = async ({ name, phone, position, company, email }) => {
    if (!auth?.access || !currentEventId) {
      return { ok: false, error: "Brak aktywnego wydarzenia." };
    }

    try {
      const response = await apiRequest(`${API_BASE}/api/contractors/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          event: currentEventId,
          name: name.trim(),
          phone: phone?.trim() || null,
          position: position?.trim() || null,
          company: company?.trim() || null,
          email: email?.trim() || null,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      const created = {
        id: data.id,
        name: data.name,
        position: data.position || "",
        company: data.company || "",
        role: formatRoleText(data.position, data.company),
        phone: data.phone || "",
        email: data.email || "",
      };

      setContractorsByEvent((previous) => {
        const eventContractors = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: [created, ...eventContractors],
        };
      });

      setForm((f) => ({ ...f, contactId: created.id }));
      showToast(`Dodano wykonawcę ${created.name}`);
      await fetchUserLimits();
      return { ok: true, contractor: created };
    } catch {
      return { ok: false, error: "Nie udało się dodać wykonawcy." };
    }
  };

  // API: update contractor details from executor view.
  const updateContractor = async (contractorId, payload) => {
    if (!auth?.access || !currentEventId) {
      return { ok: false, error: "Brak aktywnego wydarzenia." };
    }

    try {
      const response = await apiRequest(`${API_BASE}/api/contractors/${contractorId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      const updated = {
        id: data.id,
        name: data.name,
        position: data.position || "",
        company: data.company || "",
        role: formatRoleText(data.position, data.company),
        phone: data.phone || "",
        email: data.email || "",
      };

      setContractorsByEvent((previous) => {
        const eventContractors = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventContractors.map((contractor) =>
            contractor.id === contractorId ? updated : contractor
          ),
        };
      });

      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventTasks.map((task) =>
            task.contact && task.contact.id === contractorId
              ? { ...task, contact: updated }
              : task
          ),
        };
      });

      showToast(`Zaktualizowano wykonawcę ${updated.name}`);
      return { ok: true, contractor: updated };
    } catch {
      return { ok: false, error: "Nie udało się zapisać zmian wykonawcy." };
    }
  };

  const deleteContractor = async (contractorId) => {
    if (!auth?.access || !currentEventId) {
      return { ok: false, error: "Brak aktywnego wydarzenia." };
    }

    try {
      const response = await apiRequest(`${API_BASE}/api/contractors/${contractorId}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        const data = await response.json().catch(() => null);
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      setContractorsByEvent((previous) => {
        const eventContractors = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventContractors.filter((contractor) => contractor.id !== contractorId),
        };
      });

      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventTasks.map((task) =>
            task.contact && task.contact.id === contractorId
              ? { ...task, contact: null }
              : task
          ),
        };
      });

      showToast("Wykonawca został usunięty");
      return { ok: true };
    } catch {
      return { ok: false, error: "Nie udało się usunąć wykonawcy." };
    }
  };

  // API: update task status from TaskCard/Timeline/Executor actions.
  const updateTask = async (id, { desc, date, time, assignee }) => {
    if (!currentEventId) return { ok: false, error: "Brak aktywnego wydarzenia." };

    const payload = {
      name: desc.trim().slice(0, 255),
      description: desc.trim(),
      deadline: toUtcIsoFromLocalDateTime(date, time),
      contractor: null,
      assignee_user: null,
      assign_to_self: false,
    };

    if (assignee === "self") {
      payload.assign_to_self = true;
    } else if (assignee !== "unassigned") {
      payload.contractor = Number(assignee);
    }

    try {
      const response = await apiRequest(`${API_BASE}/api/tasks/${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      const updated = normalizeTask(data);
      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventTasks.map((task) => (task.id === id ? updated : task)),
        };
      });

      setActiveTask(updated);
      showToast("Zadanie zostało zaktualizowane");
      return { ok: true, task: updated };
    } catch {
      return { ok: false, error: "Nie udało się zapisać zmian zadania." };
    }
  };

  // API: update task status from TaskCard/Timeline/Executor actions.
  const setStatus = async (id, status) => {
    if (!currentEventId) return;
    const backendStatus = UI_TO_BACKEND_STATUS[status] || "new";

    try {
      const response = await apiRequest(`${API_BASE}/api/tasks/${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: backendStatus }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        showToast(normalizeErrorMessage(data));
        return;
      }

      const updated = normalizeTask(data);
      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventTasks.map((t) => (t.id === id ? updated : t)),
        };
      });
    } catch {
      showToast("Nie udało się zmienić statusu zadania.");
    }
  };

  // API: delete task from executor view.
  const deleteTask = async (id) => {
    if (!currentEventId) return { ok: false };

    try {
      const response = await apiRequest(`${API_BASE}/api/tasks/${id}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return { ok: false, error: "Sesja wygasła. Zaloguj się ponownie." };
        }
        const data = await response.json().catch(() => null);
        return { ok: false, error: normalizeErrorMessage(data) };
      }

      setTasksByEvent((previous) => {
        const eventTasks = previous[currentEventId] || [];
        return {
          ...previous,
          [currentEventId]: eventTasks.filter((task) => task.id !== id),
        };
      });

      if (activeTask?.id === id) setActiveTask(null);
      setScreen("dashboard");
      showToast("Zadanie zostało usunięte");
      return { ok: true };
    } catch {
      return { ok: false, error: "Nie udało się usunąć zadania." };
    }
  };

  const openContractorEditor = (task) => {
    if (!task?.contact) return;
    setActiveTask(task);
    setOpenContractorEditorOnEnter(true);
    setScreen("executor");
  };

  // Task filtering + counters displayed in dashboard tabs and alerts.
  const visible =
    filter === "all"
      ? tasks
      : filter === "active"
      ? tasks.filter((t) => t.status !== "done")
      : tasks.filter((t) => t.status === filter);

  const counts = Object.fromEntries(
    Object.keys(STATUS).map((s) => [
      s,
      tasks.filter((t) => t.status === s).length,
    ])
  );

  const taskFilterOptions = [
    { key: "active", label: "Aktywne", count: tasks.filter((t) => t.status !== "done").length },
    { key: "new", label: "Nowe", count: counts.new || 0 },
    { key: "in_progress", label: "W toku", count: counts.in_progress || 0 },
    { key: "problem", label: "Problem", count: counts.problem || 0 },
    { key: "done", label: "Done", count: counts.done || 0 },
    { key: "all", label: "Wszystkie", count: tasks.length },
  ];

  // Token-based task view — must be checked before the auth gate so anonymous
  // users with an SMS link bypass the login screen entirely.
  const tokenMatch = window.location.pathname.match(/\/task\/([a-f0-9-]{36})$/i);
  if (tokenMatch) {
    return <TaskTokenScreen token={tokenMatch[1]} />;
  }

  // Public contractor task list view (by UUID)
  const contractorMatch = window.location.pathname.match(/^\/([a-f0-9\-]{36})\/tasks$/i);
  if (contractorMatch) {
    return <ContractorTaskListScreen contractorUuid={contractorMatch[1]} />;
  }

  // Authentication gate: show login/register screen.
  if (!auth?.access) {
    return (
      <>
        <AuthScreen onAuthenticated={setAuth} />
      </>
    );
  }

  // Events overview screen (choose/create/archive events).
  if (screen === "user") {
    return (
      <UserScreen
        auth={auth}
        userTier={userTier}
        userLimits={userLimits}
        onLogout={logout}
        onBack={() => {
          setScreen(selectedEvent ? "dashboard" : "events");
        }}
      />
    );
  }

  if (screen === "events" || !selectedEvent) {
    return (
      <>
        {isOffline && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10000,
              background: "#1e293b",
              color: "#f8fafc",
              textAlign: "center",
              paddingTop: "calc(8px + env(safe-area-inset-top))",
              paddingBottom: "8px",
              paddingLeft: "16px",
              paddingRight: "16px",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              fontFamily: "var(--font-body)",
              letterSpacing: "0.01em",
            }}
          >
            Brak połączenia z internetem
          </div>
        )}
        <EventsScreen
          auth={auth}
          userTier={userTier}
          userLimits={userLimits}
          events={events}
          loading={eventsLoading}
          error={eventsError}
          onRefresh={fetchEvents}
          onCreateEvent={createEvent}
          onUpdateEvent={updateEvent}
          onSelect={(event) => {
            setSelectedEvent(event);
            setActiveTask(null);
            setScreen("dashboard");
          }}
          onLogout={logout}
          onArchiveEvent={archiveEvent}
          onRestoreEvent={restoreEvent}
          onProfileClick={() => setScreen("user")}
        />
      </>
    );
  }

  // Executor screen (single task details + status actions).
  if (screen === "executor" && activeTask) {
    const task = tasks.find((t) => t.id === activeTask.id) || activeTask;
    return (
      <>
        {isOffline && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10000,
              background: "#1e293b",
              color: "#f8fafc",
              textAlign: "center",
              paddingTop: "calc(8px + env(safe-area-inset-top))",
              paddingBottom: "8px",
              paddingLeft: "16px",
              paddingRight: "16px",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              fontFamily: "var(--font-body)",
              letterSpacing: "0.01em",
            }}
          >
            Brak połączenia z internetem
          </div>
        )}
        <ExecutorScreen
          auth={auth}
          userTier={userTier}
          task={task}
          contractors={contractors}
          openContractorEditorOnLoad={openContractorEditorOnEnter}
          onStatus={(s) => {
            setStatus(task.id, s);
            setActiveTask({ ...task, status: s });
          }}
          onUpdateTask={updateTask}
          onDeleteTask={deleteTask}
          onUpdateContractor={updateContractor}
          onBack={() => {
            setOpenContractorEditorOnEnter(false);
            setScreen("dashboard");
          }}
          onProfileClick={() => setScreen("user")}
        />
      </>
    );
  }

  // Main event dashboard screen.
  return (
    <>
      {isOffline && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            background: "#1e293b",
            color: "#f8fafc",
            textAlign: "center",
            paddingTop: "calc(8px + env(safe-area-inset-top))",
            paddingBottom: "8px",
            paddingLeft: "16px",
            paddingRight: "16px",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-medium)",
            fontFamily: "var(--font-body)",
            letterSpacing: "0.01em",
          }}
        >
          Brak połączenia z internetem
        </div>
      )}
      <div
        className="app-container"
        style={{
          fontFamily: "var(--font-body)",
          background: "var(--color-bg-app)",
          minHeight: "100dvh",
          width: "calc(100% - 16px)",
          margin: "0 auto",
          paddingTop: isOffline ? 36 : 0,
        }}
      >
      {/* Global toast message banner (success/error info). */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: "calc(16px + env(safe-area-inset-top))",
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text-primary)",
            padding: "12px 20px",
            borderRadius: "var(--radius-lg)",
            fontWeight: "var(--font-medium)",
            fontSize: "var(--text-sm)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "var(--color-success)", display: "inline-flex" }}>
            <UiIcon Icon={Check} />
          </span>
          {toast}
        </div>
      )}

      {/* Dashboard header: event title, back button, archive/restore, logout, add task. */}
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "20px 20px 0",
          paddingTop: "calc(20px + env(safe-area-inset-top))",
        }}
      >
        {/* Header row with event meta + primary actions. */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: selectedEvent.active ? "var(--color-success)" : "var(--color-danger)",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  fontWeight: "var(--font-medium)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {selectedEvent.active ? "Aktywne" : "Archiwalne"}
              </span>
            </div>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-h2)",
                fontWeight: "var(--font-semibold)",
                color: "var(--color-text-primary)",
                margin: 0,
              }}
            >
              {selectedEvent.name}
            </h1>
            <button
              onClick={() => {
                setScreen("events");
                setActiveTask(null);
              }}
              style={{
                marginTop: 8,
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--color-primary)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                cursor: "pointer",
              }}
            >
              <InlineIcon Icon={ArrowLeft} style={{ marginRight: 4 }} />
              Wróć do wydarzeń
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UserProfileButton auth={auth} userTier={userTier} onProfileClick={() => setScreen("user")} />
          </div>
        </div>

        {/* Filter tabs. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              minWidth: 0,
              overflowX: "auto",
            }}
          >
            <div style={{ display: "flex", gap: 0, minWidth: "max-content" }}>
              {taskFilterOptions.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: `2px solid ${filter === f.key ? "var(--color-primary)" : "transparent"}`,
                    color: filter === f.key ? "var(--color-primary)" : "var(--color-text-muted)",
                    padding: "10px 12px",
                    minHeight: 44,
                    fontSize: "var(--text-sm)",
                    fontWeight: filter === f.key ? "var(--font-semibold)" : "var(--font-medium)",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    flexShrink: 0,
                  }}
                >
                  {f.label}
                  {f.count > 0 && (
                    <span
                      style={{
                        background: filter === f.key ? "var(--color-primary-light)" : "var(--color-bg-subtle)",
                        color: filter === f.key ? "var(--color-primary)" : "var(--color-text-muted)",
                        padding: "1px 6px",
                        borderRadius: "var(--radius-full)",
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-medium)",
                      }}
                    >
                      {f.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          margin: "12px 16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              flex: 1,
              height: 38,
              borderRadius: "var(--radius-full)",
              border: "1px solid var(--color-primary-border)",
              background: "var(--color-primary)",
              color: "var(--color-bg-card)",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-semibold)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "0 14px",
              fontFamily: "inherit",
            }}
            aria-label="Odśwież zadania"
            title="Odśwież zadania"
          >
            <span className={isRefreshing ? "spin" : ""}><UiIcon Icon={RefreshCw} /></span>
            <span>Odśwież</span>
          </button>
          <button
            onClick={() => setShowCreateTaskModal(true)}
            style={{
              flex: 1,
              height: 38,
              borderRadius: "var(--radius-full)",
              border: "1px solid var(--color-primary-border)",
              background: "var(--color-primary)",
              color: "var(--color-bg-card)",
              fontSize: "var(--text-sm)",
              lineHeight: 1.2,
              fontWeight: "var(--font-medium)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "0 14px",
              fontFamily: "inherit",
            }}
            aria-label="Nowe Zadanie +"
            title="Nowe Zadanie +"
          >
            <span>Nowe Zadanie</span>
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
          </button>
          <button
            onClick={() => setShowContractorsModal(true)}
            style={{
              flex: 1,
              height: 38,
              borderRadius: "var(--radius-full)",
              border: "1px solid var(--color-primary-border)",
              background: "var(--color-primary)",
              color: "var(--color-bg-card)",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-semibold)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "0 14px",
              fontFamily: "inherit",
            }}
            aria-label="Wykonawcy"
            title="Wykonawcy"
          >
            <span>Wykonawcy</span>
          </button>
        </div>
      </div>

      {/* Main tasks content area: empty state, list view, or timeline view. */}
      <div style={{ padding: "12px 16px 32px" }}>
        {/* Empty state when no tasks exist or filter has no results. */}
        {visible.length === 0 && (
          <div style={{ textAlign: "center", padding: "64px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              <span style={{ display: "inline-flex" }}>
                <UiIcon Icon={tasks.length === 0 ? ClipboardList : Target} />
              </span>
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: "var(--font-semibold)",
                color: "var(--color-text-primary)",
                fontSize: 16,
                marginBottom: 6,
              }}
            >
              {tasks.length === 0 ? "Brak zadań" : "Brak zadań w tym filtrze"}
            </div>
            <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-body)" }}>
              {tasks.length === 0
                ? "Użyj przycisku + aby zacząć"
                : "Zmień filtr aby zobaczyć inne zadania"}
            </div>
          </div>
        )}

        {/* Timeline view grouped by date. */}
        {visible.length > 0 && (
          <TimelineView
            tasks={visible}
            event={selectedEvent}
            onOpen={(task) => {
              setOpenContractorEditorOnEnter(false);
              setActiveTask(task);
              setScreen("executor");
            }}
            onStatus={setStatus}
            onEditContractor={openContractorEditor}
          />
        )}
      </div>

      {showCreateTaskModal && (
        <div
          onClick={() => !isSubmittingTask && setShowCreateTaskModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            boxSizing: "border-box",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              borderRadius: "var(--radius-lg)",
              boxShadow: "0 16px 36px rgba(0, 0, 0, 0.24)",
            }}
          >
            <CreateScreen
              userTier={userTier}
              userLimits={userLimits}
              form={form}
              contractors={assignableContacts}
              setForm={setForm}
              onCreateContractor={createContractor}
              onSubmit={createTask}
              isSubmitting={isSubmittingTask}
              onBack={() => setShowCreateTaskModal(false)}
              isModal
            />
          </div>
        </div>
      )}

      {/* Contractors modal overlay */}
      {showContractorsModal && selectedEvent && (
        <div
          onClick={() => setShowContractorsModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            boxSizing: "border-box",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 600,
              maxHeight: "90vh",
              background: "var(--color-bg-app)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-lg)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <ContractorsScreen
              auth={auth}
              userTier={userTier}
              eventName={selectedEvent.name}
              contractors={contractors}
              tasks={tasks}
              userLimits={userLimits}
              onClose={() => setShowContractorsModal(false)}
              onRefresh={async () => {
                if (!currentEventId) return;
                await Promise.all([
                  fetchContractorsForEvent(currentEventId),
                  fetchTasksForEvent(currentEventId),
                  fetchUserLimits(),
                ]);
              }}
              onCreateContractor={createContractor}
              onUpdateContractor={updateContractor}
              onDeleteContractor={deleteContractor}
              onCreateTaskForContractor={(contractor) => {
                setForm((previous) => ({
                  ...previous,
                  contactId: contractor.id,
                  notifySms: previous.notifySms && contractor.id > 0,
                }));
                setShowContractorsModal(false);
                setShowCreateTaskModal(true);
              }}
              onOpenTask={(task) => {
                setOpenContractorEditorOnEnter(false);
                setActiveTask(task);
                setShowContractorsModal(false);
                setScreen("executor");
              }}
              isModal
            />
          </div>
        </div>
      )}
      </div>
    </>
  );
}
